import type { ActiveFilter, FilterDefinition, FilterOperator } from '@pushly/aqe/lib/components'
import type { GenericFilterValue, FilterOperator as ReportFilterOperator } from '../filter-builders/types'

/**
 * Bidirectional operator mapping between the filter bar's lowercase operators
 * and the reporting system's uppercase operators.
 *
 * The reporting system uses LIKE/NOT_LIKE where the filter bar uses
 * contains/not_contains, and has aliases (EQUALS, ANY, NOT_ANY) that
 * map to existing filter bar operators.
 */

const FILTER_TO_REPORT_OPERATOR: Record<FilterOperator, ReportFilterOperator> = {
    eq: 'EQ',
    neq: 'NEQ',
    contains: 'LIKE',
    not_contains: 'NOT_LIKE',
    in: 'IN',
    not_in: 'NOT_IN',
    any: 'ANY',
    not_any: 'NOT_ANY',
    gt: 'GT',
    gte: 'GTE',
    lt: 'LT',
    lte: 'LTE',
    between: 'BETWEEN',
    is_null: 'IS_NULL',
    is_not_null: 'IS_NOT_NULL',
}

const REPORT_TO_FILTER_OPERATOR: Record<string, FilterOperator> = {
    EQ: 'eq',
    EQUALS: 'eq',
    NEQ: 'neq',
    NOT_EQUALS: 'neq',
    LIKE: 'contains',
    NOT_LIKE: 'not_contains',
    IN: 'in',
    NOT_IN: 'not_in',
    ANY: 'any',
    NOT_ANY: 'not_any',
    GT: 'gt',
    GTE: 'gte',
    LT: 'lt',
    LTE: 'lte',
    BETWEEN: 'between',
    NOT_BETWEEN: 'between',
    IS_NULL: 'is_null',
    IS_NOT_NULL: 'is_not_null',
}

/**
 * Converts ActiveFilter[] from the filter bar to GenericFilterValue[]
 * for the reporting system.
 *
 * Mapping:
 *   ActiveFilter.key       → GenericFilterValue.field
 *   ActiveFilter.operator  → GenericFilterValue.operator (mapped via FILTER_TO_REPORT_OPERATOR)
 *   ActiveFilter.value     → GenericFilterValue.value
 */
export function toReportFilters(filters: ActiveFilter[]): GenericFilterValue[] {
    return filters.map((filter) => ({
        field: filter.key,
        operator: FILTER_TO_REPORT_OPERATOR[filter.operator] ?? filter.operator.toUpperCase(),
        value: filter.value,
    }))
}

/**
 * Converts GenericFilterValue[] from the reporting system to ActiveFilter[]
 * for the filter bar.
 *
 * Only includes filters whose field matches a known FilterDefinition key.
 *
 * Mapping:
 *   GenericFilterValue.field    → ActiveFilter.key
 *   GenericFilterValue.operator → ActiveFilter.operator (mapped via REPORT_TO_FILTER_OPERATOR)
 *   GenericFilterValue.value    → ActiveFilter.value
 */
export function fromReportFilters(filters: GenericFilterValue[], definitions: FilterDefinition[]): ActiveFilter[] {
    const knownKeys = new Set(definitions.map((d) => d.key))

    return filters
        .filter((f) => knownKeys.has(f.field))
        .map((filter) => ({
            key: filter.field,
            operator: REPORT_TO_FILTER_OPERATOR[filter.operator] ?? (filter.operator.toLowerCase() as FilterOperator),
            value: filter.value,
        }))
}
